import { commerceOfferProperties } from "@/lib/analytics-ga4";
import { trackEvent } from "@/lib/analytics";
import type { CheckoutChannel } from "@/lib/checkout-log-domain";
import type { CommerceSystem } from "@/lib/commerce-routing";

export function trackReportCheckoutStarted(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  source?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("report_checkout_started", props);
  trackEvent(
    "commerce_checkout_started",
    commerceOfferProperties({ ...props, offer_type: "report" })
  );
}

export function trackReportCheckoutAbandoned(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("report_checkout_abandoned", props);
}

export function trackReportCheckoutRecovered(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("report_checkout_recovered", props);
}

export function trackReportCheckoutRecoveryClicked(props: {
  reading_id?: string;
  plan_id?: string;
  plan_type?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
}) {
  trackEvent("report_checkout_recovery_clicked", props);
}

/**
 * Fires once when the inline checkout CONTACT form (email + phone) becomes
 * visible — i.e. BEFORE Razorpay is opened. This is the only signal for the
 * pre-payment abandonment cohort (~819 users/mo who load the form but never
 * click pay): `report_checkout_started` only fires once they tap buy, so the
 * drop between "form shown" and "checkout started" was previously invisible.
 * No PII — prefill flags are booleans only.
 */
export function trackReportCheckoutFormShown(props: {
  plan_id: string;
  price_inr?: number;
  source_path?: string;
  intent?: string;
  placement?: string;
  prefill_email?: boolean;
  prefill_phone?: boolean;
}) {
  trackEvent("report_checkout_form_shown", props);
}

/**
 * Fires when Razorpay reports a UPI-specific payment timeout — the dominant
 * failure class (~94% of failed payments). Distinct from the generic
 * `razorpay_checkout_closed` so UPI-timeout volume + recovery can be measured
 * on its own, and so the card-fallback nudge shown to the user is attributable.
 */
export function trackUpiPaymentTimeout(props: {
  plan_id: string;
  plan_type?: string;
  order_id?: string;
  source_path?: string;
  error_reason?: string;
  error_code?: string;
}) {
  trackEvent("report_upi_payment_timeout", props);
}

export function trackReportPurchased(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  source?: string;
  placement?: string;
  order_id: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("report_purchased", props);
}

export function trackRevenueCompleted(props: {
  reading_id?: string;
  revenue_inr: number;
  order_id: string;
  commerce_system: CommerceSystem;
  checkout_channel: CheckoutChannel;
  source?: string;
  source_path?: string;
  intent?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
  placement?: string;
  offer_type?: string;
  plan_id?: string;
  product_sku?: string;
  product_name?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("revenue_completed", props);
  trackEvent(
    "commerce_purchase_completed",
    commerceOfferProperties({ ...props, offer_type: props.offer_type ?? "report" })
  );
}
